import {SubmenuDivHandler} from './SubmenuDivHandler.js';
import { EventsClient } from '../events-client.js';
import { MenuController } from './menu-controller.js';
import Logger from '../utils/logger.js';
import { applyComponentStyles } from '../utils/shared-styles.js';
import { UserMessaging } from '../utils/user-messaging.js';
import {
    readSchemaButtonLabel,
    readSchemaInputPlaceholder,
    VR_MAIN_MENU_ACTIONS,
    createVrMainMenuSchema,
} from './schemas/vr-main-menu-schema.js';

const MODULE = 'VidshareSubmenu';

/** Streaming platforms that block direct <video> loading via CORS. */
const UNSUPPORTED_HOSTS: ReadonlyArray<{ pattern: RegExp; name: string }> = [
    { pattern: /(?:^|\.)youtube\.com$/i,    name: 'YouTube' },
    { pattern: /^youtu\.be$/i,              name: 'YouTube' },
    { pattern: /(?:^|\.)vimeo\.com$/i,      name: 'Vimeo' },
    { pattern: /(?:^|\.)twitch\.tv$/i,      name: 'Twitch' },
    { pattern: /(?:^|\.)dailymotion\.com$/i, name: 'Dailymotion' },
    { pattern: /(?:^|\.)tiktok\.com$/i,     name: 'TikTok' },
    { pattern: /(?:^|\.)netflix\.com$/i,    name: 'Netflix' },
];

/**
 * Returns an error string if the URL is a known streaming platform that
 * cannot be loaded as a direct video source due to CORS restrictions,
 * or null if the URL looks usable.
 */
function getUnsupportedUrlReason(url: string): string | null {
    let hostname: string;
    try {
        hostname = new URL(url).hostname.toLowerCase();
    } catch {
        return 'Invalid URL — please enter a full URL including https://';
    }
    for (const { pattern, name } of UNSUPPORTED_HOSTS) {
        if (pattern.test(hostname)) {
            return `${name} URLs are not supported. Please use a direct video file URL (MP4, WebM, HLS .m3u8, DASH .mpd, etc.)`;
        }
    }
    return null;
}

const CSS = `
        /* Style for the Video Share preview video - responsive */
        .mm-preview-video {
            width: 100%;
            max-width: min(360px, 90vw);
            height: auto;
            aspect-ratio: 3/2;
            border: 2px solid #00d9ff;
            border-radius: 12px;
            display: block;
            margin: 0.5rem auto;
            box-shadow: 0 0 20px rgba(0, 217, 255, 0.4), 0 0 40px rgba(0, 255, 157, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        @media (max-width: 768px) {
            .mm-preview-video {
                max-width: 100%;
            }
        }

        .mm-audio-mix-control {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            justify-content: center;
            width: 100%;
            margin-top: 0.5rem;
            flex-wrap: wrap;
        }

        .mm-audio-mix-label {
            color: #b8e3f0;
            font-size: clamp(0.8rem, 2.2vw, 0.95rem);
            min-width: 9rem;
            text-align: right;
        }

        .mm-audio-mix-value {
            color: #00ffff;
            font-weight: 700;
            min-width: 2.5rem;
            text-align: left;
        }

        .mm-audio-mix-slider {
            width: min(280px, 90%);
            accent-color: #00d9ff;
        }

        .mm-broadcast-mode-control {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            margin-top: 0.5rem;
        }

        .mm-broadcast-mode-label {
            color: #b8e3f0;
            font-size: clamp(0.8rem, 2.2vw, 0.95rem);
        }

        .mm-broadcast-mode-checkbox {
            width: 18px;
            height: 18px;
            accent-color: #00d9ff;
        }

        .mm-url-share-controls {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            width: 100%;
            align-items: center;
            margin-top: 0.5rem;
        }

        .mm-url-share-input {
            width: min(420px, 95%);
            padding: 0.55rem 0.7rem;
            border-radius: 8px;
            border: 1px solid rgba(0, 217, 255, 0.65);
            background: rgba(7, 15, 29, 0.88);
            color: #e8fcff;
            outline: none;
        }

        .mm-url-share-input:focus {
            border-color: #00ffff;
            box-shadow: 0 0 0 2px rgba(0, 255, 255, 0.2);
        }

`;

const MM_VIDSHARE_HTML = `
        <!-- Modal content for Vidshare menu -->
        <div id="menu-modal-vidshare" class="menu-modal-content">
            <h2>Video Sharing</h2>
            <p>Choose Portion of Screen or Camera to Share<br>
            <button id="mm-vidshare-screenshare-button" class="mm-submit-button">Screen</button>
            <button id="mm-vidshare-camerashare-button" class="mm-submit-button">Camera</button>
            <video id="mm-vidshare-preview-video" class="mm-preview-video"></video>
            </p>
            <p>Broadcast Video to Peers<br>
            <button id="mm-vidshare-broadcast-button" class="mm-submit-button">Broadcast</button>
            </p>
            <div class="mm-url-share-controls">
                <label for="mm-vidshare-url-input">Direct Stream URL (beta)</label>
                <input
                    id="mm-vidshare-url-input"
                    class="mm-url-share-input"
                    type="url"
                    placeholder="https://example.com/video-stream.mp4"
                >
                <button id="mm-vidshare-url-submit-button" class="mm-submit-button">Play URL For Everyone</button>
                <div class="mm-broadcast-mode-control">
                    <label for="mm-vidshare-url-resync-checkbox" class="mm-broadcast-mode-label">Enable periodic re-sync pings</label>
                    <input id="mm-vidshare-url-resync-checkbox" class="mm-broadcast-mode-checkbox" type="checkbox" checked>
                </div>
            </div>
            <div class="mm-broadcast-mode-control">
                <label for="mm-vidshare-server-based-checkbox" class="mm-broadcast-mode-label">Use Server-Based Broadcast (mediasoup)</label>
                <input id="mm-vidshare-server-based-checkbox" class="mm-broadcast-mode-checkbox" type="checkbox">
            </div>
            <p>
            Shared Audio in Group Mix
            <div class="mm-audio-mix-control">
                <span class="mm-audio-mix-label">Share Audio Level</span>
                <input
                    id="mm-vidshare-audio-mix-slider"
                    class="mm-audio-mix-slider"
                    type="range"
                    min="0"
                    max="2"
                    step="0.05"
                    value="1"
                >
                <span id="mm-vidshare-audio-mix-value" class="mm-audio-mix-value">1.00x</span>
            </div>
            </p>
        </div>
`;

/**
 * VidshareSubmenuDH
 */
export class VidshareSubmenuDH extends SubmenuDivHandler {
    static TypeName = 'VidshareeSubmenuDH';

    constructor() {
        super();

        // Apply video share specific styles (shared styles applied by MenuController)
        applyComponentStyles(CSS, 'vidshare-submenu');
    }

    updateConditionals(): void {
        const btnBroadcast = document.getElementById('mm-vidshare-broadcast-button') as HTMLButtonElement;

        if (btnBroadcast) {
            if (EventsClient.instance.sharingVideo) {
                btnBroadcast.innerHTML = "Stop Broadcasting";
            } else {
                btnBroadcast.innerHTML = "Broadcast";
            }
        }
    }

    displayMenuContent(containerDiv : HTMLDivElement): void {
        containerDiv.innerHTML = MM_VIDSHARE_HTML;

        const btnScreenshare = document.getElementById('mm-vidshare-screenshare-button');
        const btnCamerashare = document.getElementById('mm-vidshare-camerashare-button');
        const btnBroadcast = document.getElementById('mm-vidshare-broadcast-button');
        const btnUrlSubmit = document.getElementById('mm-vidshare-url-submit-button');
        const urlInput = document.getElementById('mm-vidshare-url-input') as HTMLInputElement;
        const urlResyncCheckbox = document.getElementById('mm-vidshare-url-resync-checkbox') as HTMLInputElement;
        const serverBasedCheckbox = document.getElementById('mm-vidshare-server-based-checkbox') as HTMLInputElement;
        const audioMixSlider = document.getElementById('mm-vidshare-audio-mix-slider') as HTMLInputElement;
        const audioMixValue = document.getElementById('mm-vidshare-audio-mix-value') as HTMLSpanElement;

        const previewVid = document.getElementById('mm-vidshare-preview-video') as HTMLVideoElement;
        previewVid?.parentNode?.replaceChild(MenuController.instance.sharePreviewVideo, previewVid);

        if (!btnScreenshare || !btnCamerashare || !btnBroadcast) {
            Logger.error(MODULE, 'Menu Controller could not find required video share action buttons in current menu content.');
            return;
        }

        const mainSchema = createVrMainMenuSchema();
        const titleEl = containerDiv.querySelector('h2');
        if (titleEl) {
            titleEl.textContent = mainSchema.title;
        }
        btnScreenshare.textContent = readSchemaButtonLabel(mainSchema, VR_MAIN_MENU_ACTIONS.chooseScreenshare, 'Screen');
        btnCamerashare.textContent = readSchemaButtonLabel(mainSchema, VR_MAIN_MENU_ACTIONS.chooseCamerashare, 'Camera');
        if (btnUrlSubmit) {
            btnUrlSubmit.textContent = readSchemaButtonLabel(mainSchema, VR_MAIN_MENU_ACTIONS.broadcastUrl, 'Play URL For Everyone');
        }
        if (urlInput) {
            urlInput.placeholder = readSchemaInputPlaceholder(mainSchema, 'vr-broadcast-url-input', urlInput.placeholder);
        }

        const updateMixValueText = (value: number) => {
            if (audioMixValue) {
                audioMixValue.innerHTML = `${value.toFixed(2)}x`;
            }
        };

        btnScreenshare.addEventListener('click', () => {
            MenuController.instance.onUserChoice.notify(VR_MAIN_MENU_ACTIONS.chooseScreenshare);
        });

        btnCamerashare.addEventListener('click', () => {
            MenuController.instance.onUserChoice.notify(VR_MAIN_MENU_ACTIONS.chooseCamerashare);
        });

        btnBroadcast.addEventListener('click', () => {
            if (EventsClient.instance.sharingVideo) {
                MenuController.instance.onUserChoice.notify("stop-broadcasting-media");
            } else {
                MenuController.instance.onUserChoice.notify("broadcast-chosen-media");
            }
        });

        if (!btnUrlSubmit || !urlInput) {
            Logger.warn(MODULE, 'URL share controls are missing from vidshare submenu; URL playback controls are disabled.');
            return;
        }

        const submitUrlPlayback = () => {
            const url = urlInput.value.trim();
            if (!url) {
                Logger.warn(MODULE, 'Cannot start URL playback: empty URL');
                return;
            }

            const unsupportedReason = getUnsupportedUrlReason(url);
            if (unsupportedReason) {
                UserMessaging.instance.notify(unsupportedReason, 6.0);
                Logger.warn(MODULE, 'Rejected unsupported URL', url, unsupportedReason);
                return;
            }

            MenuController.instance.onUserChoice.notify(VR_MAIN_MENU_ACTIONS.broadcastUrl, {
                url,
                enableResyncPings: urlResyncCheckbox ? urlResyncCheckbox.checked : true,
            });
        };

        btnUrlSubmit.addEventListener('click', submitUrlPlayback);
        urlInput.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                submitUrlPlayback();
            }
        });

        if (serverBasedCheckbox) {
            serverBasedCheckbox.checked = EventsClient.instance.serverBasedBroadcast;
            serverBasedCheckbox.addEventListener('change', () => {
                EventsClient.instance.serverBasedBroadcast = serverBasedCheckbox.checked;
            });
        }

        if (audioMixSlider) {
            const initialGain = EventsClient.instance.getBroadcastShareAudioMixGain();
            audioMixSlider.value = String(initialGain);
            if (audioMixValue) {
                updateMixValueText(initialGain);
            }
            audioMixSlider.addEventListener('input', () => {
                const parsedGain = Number(audioMixSlider.value);
                const gain = Number.isFinite(parsedGain) ? parsedGain : 1;
                EventsClient.instance.setBroadcastShareAudioMixGain(gain);
                if (audioMixValue) {
                    updateMixValueText(gain);
                }
            });
        }
    }

    hideContent(): void {
        const btnScreenshare = document.getElementById('mm-vidshare-screenshare-button');
        const btnCamerashare = document.getElementById('mm-vidshare-camerashare-button');
        const btnBroadcast = document.getElementById('mm-vidshare-broadcast-button');
        const btnUrlSubmit = document.getElementById('mm-vidshare-url-submit-button');
        const urlInput = document.getElementById('mm-vidshare-url-input');
        const urlResyncCheckbox = document.getElementById('mm-vidshare-url-resync-checkbox');
        const serverBasedCheckbox = document.getElementById('mm-vidshare-server-based-checkbox');
        const audioMixSlider = document.getElementById('mm-vidshare-audio-mix-slider');

        var newBtn = btnScreenshare?.cloneNode(true);
        if (newBtn) {
            btnScreenshare?.parentNode?.replaceChild(newBtn, btnScreenshare);
        }
        newBtn = btnCamerashare?.cloneNode(true);
        if (newBtn) {
            btnCamerashare?.parentNode?.replaceChild(newBtn, btnCamerashare);
        }
        newBtn = btnBroadcast?.cloneNode(true);
        if (newBtn) {
            btnBroadcast?.parentNode?.replaceChild(newBtn, btnBroadcast);
        }
        newBtn = btnUrlSubmit?.cloneNode(true);
        if (newBtn) {
            btnUrlSubmit?.parentNode?.replaceChild(newBtn, btnUrlSubmit);
        }
        newBtn = urlInput?.cloneNode(true);
        if (newBtn) {
            urlInput?.parentNode?.replaceChild(newBtn, urlInput);
        }
        newBtn = urlResyncCheckbox?.cloneNode(true);
        if (newBtn) {
            urlResyncCheckbox?.parentNode?.replaceChild(newBtn, urlResyncCheckbox);
        }
        newBtn = serverBasedCheckbox?.cloneNode(true);
        if (newBtn) {
            serverBasedCheckbox?.parentNode?.replaceChild(newBtn, serverBasedCheckbox);
        }
        newBtn = audioMixSlider?.cloneNode(true);
        if (newBtn) {
            audioMixSlider?.parentNode?.replaceChild(newBtn, audioMixSlider);
        }
    }
}
